import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowParameterValueErrorType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

const methods = new Map([
  ['max', 'MAX($1) AS $1_max'],
  ['min', 'MIN($1) AS $1_min'],
  ['count', 'COUNT($1) AS $1_count'],
  ['avg', 'AVG($1) AS $1_avg'],
  ['any', 'ANY_VALUE($1) AS $1_any'],
  ['sum', 'SUM($1) AS $1_sum']
])

class IntersectRaster extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'carto.intersectraster',
        group: 'Raster Operations',
        title: 'Intersect and Aggregate Raster',
        description: `
      This component intersects a raster table with a table containing polygons and
      aggregates the values extracted from the intersection areas.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Raster table'
        },
        {
          name: 'vector',
          type: WorkflowComponentParamType.InputTable,
          title: 'Vector table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'vector',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'columns',
          type: WorkflowComponentParamType.SelectColumnAggregation,
          allowExpression: false,
          title: 'Raster aggregated properties',
          placeholder: 'workflows.parameterForm.selectAField',
          parent: 'source',
          optional: true
        },
        {
          name: 'addcustomexpression',
          type: WorkflowComponentParamType.Boolean,
          title: 'Add custom expression',
          optional: true,
          default: false
        },
        {
          name: 'expression',
          type: WorkflowComponentParamType.StringSql,
          title: '',
          optional: true,
          placeholder: 'SUM(band_1 + band_2) AS total_sum',
          showIf: [{ parameter: 'addcustomexpression', value: true }]
        },
        {
          name: 'groupby_columns',
          type: WorkflowComponentParamType.Column,
          title: 'Group by columns',
          parent: 'vector',
          optional: true,
          advanced: true,
          mode: WorkflowComponentParameterMode.Multiple
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams) {
    let query = ''
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const prepParams = this._prepareParameters(params)
    const sourceTable =
      codeType === WorkflowCodeType.StoredProcedure && connection.type === Provider.BigQuery
        ? `${connection.tempLocation}.${tempTableName(componentName, prepParams.source as string)}`
        : (prepParams.source as string)
    const writeSourceTable =
      codeType === WorkflowCodeType.StoredProcedure && connection.type === Provider.BigQuery
        ? connection.getCreateTempTableQuery(
            sourceTable,
            `SELECT * FROM ${connection.quoteName(prepParams.source as string)}`,
            WorkflowCodeType.Full,
            null
          )
        : ''
    let expression
    const columns = prepParams.columns as string
    if (columns) {
      const tokens: Array<string> = ((prepParams.columns as string) ?? '').split(',').map((x) => x.trim())
      const stats: Array<string> = []
      for (let index = 0; index < tokens.length; index += 2) {
        stats.push((methods.get(tokens[index + 1]) as string).replace(/\$1/g, tokens[index]))
      }
      expression = stats.join(',')
    }
    if (prepParams.expression && expression) {
      expression = `${expression},${prepParams.expression}`
    } else if (prepParams.expression) {
      expression = prepParams.expression as string
    }
    expression = expression.replace(/\n/g, '')
    const nullValue = connection.type === Provider.DatabricksRest ? "''" : 'NULL'
    const options =
      prepParams.groupby_columns && (prepParams.groupby_columns as Array<string>).length > 0
        ? `'{"groupby_columns": ${JSON.stringify(prepParams.groupby_columns)}}'`
        : nullValue
    const tempOutputTable = `${connection.tempLocation}.${tempTableName(componentName, expression)}`
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    if (!schemas) {
      return null
    }
    const vectorSchema = schemas.input['vector']
    if (!vectorSchema) {
      return null
    }

    let nonGeomCols = ''
    if (connection.type === Provider.DatabricksRest) {
      nonGeomCols = (vectorSchema as TableSchema)
        .filter((c) => c.name !== prepParams.geo)
        .map((c) => c.name)
        .join(', ')
    } else {
      nonGeomCols = (vectorSchema as TableSchema)
        .filter((c) => c.type !== ColumnDataType.Geography)
        .map((c) => c.name)
        .join(', ')
    }

    const inputQuery = `SELECT ${prepParams.geo} AS geom${
      nonGeomCols ? `, ${nonGeomCols}` : ''
    } FROM ${connection.quoteName(prepParams.vector as string)}`
    switch (connection.type) {
      case Provider.BigQuery: {
        query = dedent`
            BEGIN
              ${writeSourceTable}
              DROP TABLE IF EXISTS ${connection.quoteName(storedProc ? tempOutputTable : outputTablename)};
              CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.RASTER_AGG_VALUES(
                  '${sourceTable}',
                  '${inputQuery}',
                  '${expression}',
                  '${storedProc ? tempOutputTable : outputTablename}',
                  ${options}
              );
              ${
                storedProc
                  ? dedent`
                    CREATE TEMP TABLE ${outputTablename} AS
                    SELECT * FROM ${connection.quoteName(tempOutputTable)};
                    DROP TABLE ${connection.quoteName(tempOutputTable)};
                    DROP TABLE ${connection.quoteName(sourceTable)};
                  `
                  : ''
              }
            END;`

        break
      }

      case Provider.Snowflake: {
        query = dedent`
            BEGIN
              DROP TABLE IF EXISTS ${connection.quoteName(outputTablename)};
              CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.RASTER_AGG_VALUES(
                  '${sourceTable}',
                  '${inputQuery}',
                  '${expression}',
                  '${outputTablename}',
                  ${options}
              );
            END;`
        break
      }
      case Provider.DatabricksRest: {
        query = dedent`
          spark.read.format("com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource").option(
              "sql",
              """
              CALL_CARTO carto_un.carto.RASTER_AGG_VALUES(
                '${sourceTable}',
                '${inputQuery}',
                '${expression}',
                '${outputTablename}',
                ${options}
              );
              """
            ).load().collect();`
        break
      }
    }
    query = connection.wrapCodeInIfTableExistsBlock(query, [outputTablename], codeType)

    if (connection.type === Provider.DatabricksRest) {
      query = dedent`
      ${query}
      ${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(connection.optimizationStatsQuery(outputTablename))}
    `
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (resolvedParamValues.columns == null && resolvedParamValues.expression == null) {
      problems.push({
        parameter: 'expression',
        type: WorkflowParameterValueErrorType.MissingValue,
        message: 'At least one of "expression" and "columns" parameters must be specified'
      })
    }

    return problems
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    const classifiedMethods: Record<string, Array<string>> = {}
    classifiedMethods[ColumnDataType.Binary] = Array.from(methods.keys())
    return {
      methods: classifiedMethods
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Snowflake:
        return '1.0.0'
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const prepParams = this._prepareParameters(params)

    const schema: TableSchema = columns.vector.filter((c) => c.type !== ColumnDataType.Geography)

    let expression
    const columnsParam = prepParams.columns as string
    if (columnsParam) {
      const tokens: Array<string> = (columnsParam ?? '').split(',').map((x) => x.trim())
      const stats: Array<string> = []
      for (let index = 0; index < tokens.length; index += 2) {
        stats.push((methods.get(tokens[index + 1]) as string).replace(/\$1/g, tokens[index]))
      }
      expression = stats.join(',')
    }
    if (prepParams.expression && expression) {
      expression = `${expression},${prepParams.expression}`
    } else if (prepParams.expression) {
      expression = prepParams.expression as string
    }
    expression = expression.replace(/\n/g, '')

    const expressionCols = expression.split(',').map((x) => x.split(' ').pop())

    for (const col of expressionCols) {
      schema.push({
        name: col,
        type: ColumnDataType.Number
      })
    }

    return schema
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake]
  }
}

export { IntersectRaster }
